// apps/web/src/pages/spread/SpreadSettingsDialog.tsx
//
// Phase 9 Plan 09-12 Task 2 — Spread settings dialog (D-15).
//
// COMPOSITION PER UI-SPEC §"Spread settings dialog" (lines 404-412):
//   <Dialog>
//     <DialogHeader> + <Tabs defaultValue="sharing">
//       <TabsList>: Sharing / Vocabulary
//       <TabsContent value="sharing"><SharingTab /></TabsContent>
//       <TabsContent value="vocabulary"><VocabularyTab /></TabsContent>
//     <DialogFooter>: Cancel (outline, autoFocus) + Save changes (outline — NOT primary)
//
// THE F-C-1 CARRY-FORWARD (Phase 8 UI-REVIEW):
//   Save changes is `variant="outline"` — explicitly NOT primary. The global
//   Save on the SpreadComposerToolbar is the ONLY primary CTA in chrome.
//   This avoids two competing primary CTAs in the same view.
//
// DISCARD-CHANGES INLINE CONFIRMATION (UI-SPEC line 412):
//   When closing with a dirty form, an inline aria-live region shows
//   "Discard changes?" + Yes/No buttons. The default DialogClose is intercepted
//   to enter this state instead of closing. Once confirmed, the dialog closes
//   and the form is rolled back to its baseline (we use form.reset()).
//
// FORM-STATE DISCIPLINE:
//   The dialog mounts INSIDE the SpreadComposer's <FormProvider> so both
//   tabs use the same RHF context. The Save button does NOT trigger the
//   spread save mutation directly — it just closes the dialog (the global
//   Save on the toolbar handles the actual POST/PUT). Save vs Cancel
//   semantic difference: Save commits the dialog's local edits to the
//   parent form (which then becomes dirty); Cancel rolls them back to the
//   dialog's open-time baseline.

import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTerm } from "@/lib/vocabulary/useTerm";
import { SharingTab } from "./settings/SharingTab";
import { VocabularyTab } from "./settings/VocabularyTab";

export interface SpreadSettingsDialogProps {
  /** Controlled-open flag. SpreadComposer owns the state. */
  open: boolean;
  /** Setter for the open flag. */
  onOpenChange: (open: boolean) => void;
  /** Spread id — passed to SharingTab for the grants endpoints. */
  spreadId: string;
  /** Optional tab to open by default. */
  defaultTab?: "sharing" | "vocabulary";
}

export function SpreadSettingsDialog(props: SpreadSettingsDialogProps): React.ReactElement {
  const container = useTerm("container");
  const { formState, reset, getValues } = useFormContext();
  const [armDiscard, setArmDiscard] = useState(false);
  // Snapshot the form values at OPEN time so Cancel rolls back to that exact
  // state (not the original mount-time defaults — the dialog might be opened
  // after the user has already made some toolbar-level edits).
  const baselineRef = useRef<Record<string, unknown> | null>(null);

  useEffect(() => {
    if (props.open) {
      baselineRef.current = getValues();
      setArmDiscard(false);
    }
  }, [props.open, getValues]);

  function handleCancel(): void {
    if (formState.isDirty) {
      // Inline two-stage discard confirm (UI-SPEC line 412).
      if (!armDiscard) {
        setArmDiscard(true);
        return;
      }
      // Confirmed: roll back to the baseline snapshot and close.
      if (baselineRef.current !== null) {
        reset(baselineRef.current);
      }
    }
    setArmDiscard(false);
    props.onOpenChange(false);
  }

  function handleKeepEditing(): void {
    setArmDiscard(false);
  }

  function handleSave(): void {
    // Save here just COMMITS the dialog edits to the parent form. The
    // toolbar Save button on SpreadComposer fires the actual mutation.
    setArmDiscard(false);
    props.onOpenChange(false);
  }

  function handleOpenChange(next: boolean): void {
    // Intercept the "close on overlay click / Esc" path so dirty edits
    // surface the discard confirm instead of silently rolling back.
    if (!next && formState.isDirty && !armDiscard) {
      setArmDiscard(true);
      return;
    }
    if (!next) {
      setArmDiscard(false);
    }
    props.onOpenChange(next);
  }

  return (
    <Dialog open={props.open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{container} settings</DialogTitle>
          <DialogDescription className="sr-only">
            Configure sharing and vocabulary for this {container.toLowerCase()}.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue={props.defaultTab ?? "sharing"} className="flex flex-col gap-4">
          <TabsList className="self-start">
            <TabsTrigger value="sharing">Sharing</TabsTrigger>
            <TabsTrigger value="vocabulary">Vocabulary</TabsTrigger>
          </TabsList>

          <TabsContent value="sharing" className="mt-0">
            <SharingTab spreadId={props.spreadId} />
          </TabsContent>
          <TabsContent value="vocabulary" className="mt-0">
            <VocabularyTab />
          </TabsContent>
        </Tabs>

        {armDiscard ? (
          <div
            className="flex items-center justify-between gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3"
            aria-live="polite"
            data-discard-confirm
          >
            <span className="text-sm font-medium">Discard changes?</span>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" onClick={handleKeepEditing}>
                Keep editing
              </Button>
              <Button type="button" variant="outline" onClick={handleCancel}>
                Yes, discard
              </Button>
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" autoFocus onClick={handleCancel}>
            Cancel
          </Button>
          {/*
            UI-SPEC §"Spread settings dialog" line 410 — Save changes is
            variant="outline" (NEVER variant="default"). The global Save on
            the SpreadComposer toolbar is the only primary CTA in chrome
            (F-C-1 carry-forward from Phase 8 UI-REVIEW).
           */}
          <Button type="button" variant="outline" onClick={handleSave}>
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
SpreadSettingsDialog.displayName = "SpreadSettingsDialog";
